<template>
    <div class="space-y-6">
        <form-element name="name" title="Name or Alias">
            <text-input type="text" name="name" id="name" placeholder="Anonymous" v-model.trim="name" />
        </form-element>
        <form-element name="amount" title="Amount">
            <div class="w-full relative">
                <input class="w-full border-2 text-lg text-white p-2 border-gradient-to-br from-purple to-blue bg-dark-blue focus:outline-none focus:from-yellow focus:via-white focus:to-yellow pl-5" digitOnly type="number" name="amount" id="amount" :value="amount" v-on:input="handleInputEvent($event)" min="1" step="0.01" lang="en" inputmode="decimal">
                <span class="absolute text-white text-lg left-2 top-[9.5px] select-none">$</span>
            </div>
            <template #info v-if="props.characters">
                Need help deciding how much to donate? Try the following preset amounts. <a href="#" @click.prevent="setOpenModal('amount-descriptions')">What do these mean?</a>
            </template>
        </form-element>
        <div class="text-white" v-if="props.characters">
            <div
                class="flex justify-between items-center p-2"
                v-for="(preset, index) in donationPresets"
                :key="preset.name"
                :class="index % 2 ? 'bg-black' : ''"
            >
                <template v-if="preset.type == 'amount'">
                    <div><span class="font-bold">{{ formatMoney(preset.amount) }}</span> - {{ preset.title }}</div>
                    <donation-button @click="setAmount(preset.amount)">Select</donation-button>
                </template>
                <template v-if="preset.type == 'modal'">
                    <div><span class="font-bold">{{ preset.title }}</span></div>
                    <donation-button @click="setOpenModal(preset.modal)">View List</donation-button>
                </template>
            </div>
        </div>
        <form-element name="comment" title="Comment">
            <text-area name="comment" id="comment" rows="4" v-model.trim="comment" />
        </form-element>
        <form-element v-if="props.dtd" name="company" title="Employer (Donation Matching)">
            <div id='dd-company-name-input' class="bg-white p-2 rounded"></div>
        </form-element>
    </div>

    <Dialog title="Special Totals" description="Manipulate the donation total to your liking!" :isOpen="isPresetsOpen" @close="setIsOpen">
        <div class="text-white">
            <div
                class="flex justify-between items-center p-2"
                v-for="(preset, index) in presets"
                :key="preset.name"
                :class="index % 2 ? 'bg-black' : ''"
            >
                <div><span class="font-bold">{{ formatMoney(preset.amount) }}</span> - {{ preset.name }} ({{ formatMoney(preset.total) }})</div>
                <donation-button @click="setAmount(preset.amount)">Select</donation-button>
            </div>
        </div>
    </Dialog>

    <Dialog title="Secret Sounds" description="The following sounds have been unlocked:" :isOpen="isSecretSoundsOpen" @close="setIsOpen">
        <div class="text-white">
            <div
                class="flex justify-between items-center p-2"
                v-for="(sound, index) in sounds"
                :key="sound.name"
                :class="index % 2 ? 'bg-black' : ''"
            >
                <div><span class="font-bold">{{ formatMoney(sound.amount) }}</span> - {{ sound.name }}</div>
                <donation-button @click="setAmount(sound.amount)">Select</donation-button>
            </div>
        </div>
    </Dialog>

    <Dialog title="Preset Amounts" :isOpen="isAmountDescriptionsOpen" @close="setIsOpen">
        <div class="text-white space-y-4">
            <div
                v-for="amountDescription in amountDescriptions"
                :key="amountDescription.title"
            >
                <div class="font-bold" v-text="amountDescription.title"></div>
                <p v-text="amountDescription.description"></p>
            </div>
        </div>
    </Dialog>
</template>

<script setup>
import { inject, ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { formatMoney } from '../../utils';

import FormElement from './ui/FormElement.vue';
import DonationButton from './ui/Button.vue';
import TextInput from './ui/TextInput.vue';
import TextArea from './ui/TextArea.vue';
import Dialog from './ui/Dialog.vue';

const props = defineProps({
    dtd: Boolean,
});

const name = inject('name');
const amount = inject('amount');
const comment = inject('comment');
const employer = inject('employer');
const employerFields = inject('employerFields');

let observer = null;

onMounted(() => {
    if(props.dtd) {
        observer = new MutationObserver((mutationList, observer) => {
            mutationList.forEach((mutation) => {
                if (mutation.type == 'childList') {
                    if (mutation.addedNodes.length > 0) {
                        mutation.addedNodes.forEach((node) => {
                            if (node.classList && node.classList.contains('dtd-company-selected')) {
                                employer.value = node.querySelector('.dtd-company-selected-name').innerText;
                                const inputValues = []
                                document.querySelector('.dtd-streamlined-plugin').querySelectorAll('input').forEach((input) => {
                                    inputValues.push({
                                        name: input.name,
                                        value: input.value
                                    });
                                });
                                employerFields.value = inputValues;
                                console.log(inputValues)
                            } else {
                                employer.value = '';
                                employerFields.value = [];
                            }
                        });
                    }
                }
            });
        });
        observer.observe(document.querySelector('#dd-company-name-input'), { childList: true, subtree: true })
    }
})

onUnmounted(() => {
    if(props.dtd) {
        observer.disconnect();
    }
});

const setAmount = (value) => {
    amount.value = value;
    setOpenModal(null);
}

const openModal = ref(null);
const setOpenModal = (modal) => {
    openModal.value = modal;
}
const isPresetsOpen = computed(() => openModal.value == 'presets');
const isSecretSoundsOpen = computed(() => openModal.value == 'sounds');
const isAmountDescriptionsOpen = computed(() => openModal.value == 'amount-descriptions');
const setIsOpen = () => {
    openModal.value = null;
}

const abortController = ref(null);
const presets = ref([]);
const sounds = ref([]);

const donationPresets = [
    // {
    //     type: 'amount',
    //     amount: 20,
    //     title: 'Prize Block'
    // },
    // {
    //     type: 'amount',
    //     amount: 50,
    //     title: 'Grand Prize'
    // },
    {
        type: 'modal',
        modal: 'sounds',
        title: 'Secret Sounds'
    },
    {
        type: 'modal',
        modal: 'presets',
        title: 'Special Totals (Palindrome, etc)'
    }
]

const amountDescriptions = [
    {
        title: 'Secret Sounds',
        description: 'Certain donation amounts trigger a sound on stream! Choose one of the unlocked sounds to guarantee that it’s played on stream.'
    },
    {
        title: 'Palindrome',
        description: 'Make the on-stream donation total a palindrome! For example, $111.00 => $123.21'
    },
    {
        title: 'Step-up',
        description: 'Make the donation total a sequence of rising numbers. For example, $200.00 => $234.56'
    },
    {
        title: 'Step-down',
        description: 'Make the donation total a sequence of descending numbers. For example, $501.34 => $654.32'
    },
    {
        title: 'Repeating Digits',
        description: 'Make the donation total a sequence of the same number. For example, $501.34 => $555.55'
    },
    {
        title: 'Team Fixer',
        description: 'Rounds up the donation total a clean number. For example, $501.34 => $503.00'
    },
    {
        title: 'Team Breaker',
        description: 'Ensures the donation total ends in a random, non-patterned number. For example, $501.00 => $502.17'
    },
    // {
    //     title: 'Prize Block',
    //     description: 'Enters the amount needed to earn you an entry into one of our game prize blocks. For example, $20.00.'
    // },
    // {
    //     title: 'Grand Prize',
    //     description: 'Enters the amount needed to earn you an entry into our grand prize. For example, $50.00.'
    // }
]

watch(openModal, (newValue) => {
    if (abortController.value) {
        abortController.value.abort();
    }

    abortController.value = new AbortController();

    if (newValue == 'presets') {
        fetch('/presets', { signal: abortController.value.signal })
            .then(response => response.json())
            .then(value => {
                presets.value = value;
            })
            .catch(() => { /* Do nothing */});
    } else if (newValue == 'sounds') {
        fetch('/secret-sounds', { signal: abortController.value.signal })
            .then(response => response.json())
            .then(value => {
                sounds.value = value;
            })
            .catch(() => { /* Do nothing */});
    }
})
const handleInputEvent = (e) => {
    amount.value = transform(e.target.value);
}
const transform = (val) => {
    val = trimLeadingChars(val);
    val = trimTrailingChars(val);
    // continue your custom logic here
    
    return val;
}
const trimLeadingChars = (val) => {
    if (!val) {
        return "";
    }
    for (let i = 0; i < val.length; i++) {
        if (!isNaN(val[i])) {
            return val.slice(i);
        }
    }
    return val;
}
const trimTrailingChars = (val) => {
    if (!val) {
        return "";
    }
    for (let i = val.length - 1; i >= 0; i--) {
        if (!isNaN(Number(val[i]))) {
            return val.slice(0,i+1);
        }
    }
    
    return val;
}
</script>